import {
    Box, Collapse,
    DialogActions, DialogContent,
    DialogTitle, Divider,
    FormHelperText, InputAdornment,
    InputLabel, MenuItem, Select,
    TextField, Typography,
    Unstable_Grid2 as Grid, Link, CardHeader, Button
} from "@mui/material"
import { useForm } from "react-hook-form"
import { LoadingButton } from "@/components/common/LoadingButton";
import { FormTextField } from "@/components/HookForm";
import { dialogClose, dialogOpen } from "@/modules/MuiDialog";
import { toast } from "react-toastify";
import { patchMallBusinessInfo } from "@/services/mall.service";
import { banks } from "@/constant/banks";
import { useEffect, useMemo, useState } from "react";
import SquareThumbInput from "@/components/FileInput/SquareThumbInput";
import { uploadImageToStorage } from "@/services/proxy.service";
import { Check as ConfirmIcon } from "@mui/icons-material";
import { confirmVerifyPhoneCode, requestVerifyPhoneCode } from "@/services/open-mall.service";
import delay from "@/utils/delay";
import useInterval from "@/hooks/useInterval";
import DialogTitleBox from "@/modules/MuiDialog/DialogTitleBox";
import { useTranslation } from "react-i18next";
import { formatPhoneNumber } from "@/utils/parser";
import { formatBusinessRegistrationNumber } from "@/utils/formatBusinessNumber";
import AddressSearchModal from "@/components/AddressSearchModal";

/**
 * 통신판매번호를 "2022-서울양천-0328"(년도-지역-번호) 형태로 정규화.
 * - "2022-서울양천-0328", "2022서울양천0328" 모두 "2022-서울양천-0328"로 변환
 * - 과거 "제 ...호" 형태로 저장된 값도 제/호 제거 후 정규화
 */
export const normalizeOnlineSalesNumber = (raw?: string) => {
    const cleaned = (raw || '')
        .trim()
        .replace(/^제\s*/, '')
        .replace(/호\s*$/, '')
        .trim();
    if (!cleaned) return '';
    // 하이픈/공백 제거 후 앞 숫자(년도)/가운데 텍스트(지역)/뒤 숫자(번호)로 분리해 하이픈으로 재결합
    // 년도가 없는 입력(예: "서울양천1235" → "서울양천-1235")도 처리
    const compact = cleaned.replace(/[-\s]/g, '');
    const m = compact.match(/^(\d*)(.*?)(\d*)$/);
    if (!m) return cleaned;
    const parts = [m[1], m[2], m[3]].filter((p) => !!p);
    return parts.length ? parts.join('-') : cleaned;
};

interface BusinessInfoType {
    businessType?: string;
    businessItem?: string;
    companyName?: string;
    ownerName?: string;
    businessRegistrationNumber?: string;
    mailOrderSalesRegistrationNumber?: string;
    managerPhoneNumber?: string;
    customerCenterNumber?: string;
    companyAddress?: string;
    mallOpenBusinessType?: string;
    vendorRecommenderCode?: string;
    businessRegistrationCertificateUrl?: string;
    mailOrderSalesRegistrationCertificateUrl?: string;
}
interface BusinessInfoDialogProps extends BusinessInfoType {
    mallId: string;
}

export const BusinessInfoDialog = (props: BusinessInfoDialogProps & Record<string, any>) => {
    const { t } = useTranslation();
    const businessType = props?.mallOpenBusinessType || 'VENDOR';
    const form = useForm<BusinessInfoType>({
        defaultValues:
            businessType === 'SELLER'
                ? {
                      businessType: props?.businessType || '',
                      businessItem: props?.businessItem || '',
                      companyName: props?.companyName || '',
                      ownerName: props?.ownerName || '',
                      managerPhoneNumber: formatPhoneNumber(props?.managerPhoneNumber || ''),
                      customerCenterNumber: formatPhoneNumber(props?.customerCenterNumber || ''),
                      companyAddress: props?.companyAddress || '',
                      businessRegistrationNumber: formatBusinessRegistrationNumber(props?.businessRegistrationNumber || ''),
                      mailOrderSalesRegistrationNumber: normalizeOnlineSalesNumber(props?.mailOrderSalesRegistrationNumber),
                      // businessRegistrationCertificateUrl: props?.businessRegistrationCertificateUrl || '',
                      // mailOrderSalesRegistrationCertificateUrl: props?.mailOrderSalesRegistrationCertificateUrl || '',
                  }
                : {
                      businessType: '',
                      businessItem: '',
                      companyName: '',
                      ownerName: '',
                      managerPhoneNumber: '',
                      customerCenterNumber: '',
                      companyAddress: '',
                      businessRegistrationNumber: '',
                      mailOrderSalesRegistrationNumber: '',
                      // businessRegistrationCertificateUrl: '',
                      // mailOrderSalesRegistrationCertificateUrl: '',
                  },
    });
    const {
        register,
        watch,
        getValues,
        handleSubmit,
        trigger,
        getFieldState,
        formState: { isValid, isDirty, errors, isSubmitting },
    } = form;

    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [brnPaperFile, setBrnPaperFile] = useState<File | Blob | undefined>();
    const [mailOrderRegistrationPaperFile, setMailOrderRegistrationPaperFile] = useState<File | Blob | undefined>();

    const [isConfirmedPhone, setIsConfirmedPhone] = useState<boolean>(false);
    const [isConfirmedCode, setIsConfirmedCode] = useState<boolean>(false);
    const [isWaitingPhoneCode, setIsWaitingPhoneCode] = useState<boolean>(false);
    const [phoneCode, setPhoneCode] = useState('');
    const [isWaitConfirmCode, setIsWaitConfirmCode] = useState(false);
    const [codeRequestCount, setCodeRequestCount] = useState(0);
    const [verifyLimitSec, setVerifyLimitSec] = useState(60);

    const displayLimitTime = useMemo(() => {
        const limitSec = verifyLimitSec;

        const h = Math.floor(limitSec / (60 * 60));
        const m = Math.floor((limitSec / 60) % 60);
        const s = limitSec % 60;

        // return `${`${hh}`.padStart(2, '0')}:${mm}:${ss}`;

        const hh = h < 0 ? `${`${h}`.padStart(2, '0')}:` : '';
        const mm = `${m}`.padStart(2, '0');
        const ss = `${s}`.padStart(2, '0');

        return `${hh}${mm}:${ss}`;
    }, [verifyLimitSec]);

    function resetVerifyTimer() {
        setVerifyLimitSec(60);
    }
    useEffect(() => {
        resetVerifyTimer();
        setIsConfirmedCode(false);
    }, [codeRequestCount]);

    useInterval(() => {
        if (!codeRequestCount || !verifyLimitSec || isConfirmedCode) return;

        setVerifyLimitSec(verifyLimitSec - 1);
    }, 1000);

    async function formSubmit(data: any) {
        if (businessType !== 'SELLER') {
            if (!isConfirmedPhone) {
                toast.error(t('mall.pleaseAuthenticateManagerContact'));
                return;
            }
            if (!brnPaperFile) {
                toast.error(t('mall.pleaseSelectBusinessRegistrationCopy'));
                return;
            }
            if (!mailOrderRegistrationPaperFile) {
                toast.error(t('mall.pleaseSelectMailOrderRegistrationCopy'));
                return;
            }
        } else {
            // 포맷(하이픈) 차이는 무시하고 숫자만 비교해 실제 번호 변경 여부 판단
            const origPhoneDigits = (props?.managerPhoneNumber || '').replace(/\D/g, '');
            const curPhoneDigits = (watch().managerPhoneNumber || '').replace(/\D/g, '');
            if (origPhoneDigits !== curPhoneDigits) {
                if (!isConfirmedPhone) {
                    toast.error(t('mall.pleaseAuthenticateManagerContact'));
                    return;
                }
            }
        }
        // 통신판매번호: 하이픈 유무와 관계없이 "년도-지역-번호" 형태로 정규화하여 저장
        data = {
            ...data,
            mailOrderSalesRegistrationNumber: normalizeOnlineSalesNumber(data?.mailOrderSalesRegistrationNumber),
        };
        const businessRegistrationCertificateUrl = !!brnPaperFile
            ? await uploadImageToStorage([brnPaperFile], 1500).then((r) => (!!r ? r.url[0] : ''))
            : '';
        const mailOrderSalesRegistrationCertificateUrl = !!mailOrderRegistrationPaperFile
            ? await uploadImageToStorage([mailOrderRegistrationPaperFile], 1500).then((r) => (!!r ? r.url[0] : ''))
            : '';
        try {
            const isBrnPaperFile = !!brnPaperFile ? { ...data, businessRegistrationCertificateUrl } : { ...data };
            const payload = !!mailOrderRegistrationPaperFile
                ? { ...isBrnPaperFile, mailOrderSalesRegistrationCertificateUrl }
                : { ...isBrnPaperFile };
            await patchMallBusinessInfo(props.mallId, { ...payload });
            toast.success(t('common.updated'));
            dialogClose();
        } catch (error) {
            toast.error(t('mall.businessInfoUpdateFailed'));
        }
    }
    return (
        <>
            {/* <DialogTitle>사업자정보 수정</DialogTitle> */}
            <DialogTitleBox title={t('mall.editBusinessInfo')} />
            <DialogContent>
                <Grid container>
                    <Grid xs={12}>
                        <FormTextField
                            label={t('mall.companyTradeName')}
                            name={'companyName'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterCompanyTradeName')}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.companyTradeNameRequired')]}
                        />
                        <FormTextField
                            label={t('mall.ownerName')}
                            name={'ownerName'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterOwnerName')}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.ownerNameRequired')]}
                        />
                        <FormTextField
                            label={t('mall.businessRegistrationNumber')}
                            name={'businessRegistrationNumber'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={'000-00-00000'}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.businessRegistrationNumberRequired')]}
                            onChange={(value: string) => {
                                form.setValue('businessRegistrationNumber', formatBusinessRegistrationNumber(value || ''), {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                            }}
                        />
                        <FormTextField
                            label={t('mall.businessType')}
                            name={'businessType'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterBusinessType')}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.businessTypeRequired')]}
                        />
                        <FormTextField
                            label={t('mall.businessItem')}
                            name={'businessItem'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterBusinessItem')}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.businessItemRequired')]}
                        />
                        <FormTextField
                            label={t('mall.mailOrderSalesNumber')}
                            name={'mailOrderSalesRegistrationNumber'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={'년도-지역-4자리번호'}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.mailOrderSalesNumberRequired')]}
                        />
                        <FormTextField
                            label={t('mall.companyAddress')}
                            name={'companyAddress'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={t('mall.enterCompanyAddress')}
                            fullWidth
                            required
                            rules={[(v) => (!!v && !!v.trim()) || t('mall.companyAddressRequired')]}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Button
                                            variant="outlined"
                                            color="inherit"
                                            onClick={(ev) => {
                                                ev.stopPropagation();
                                                setAddressModalOpen(true);
                                            }}
                                            sx={{
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                flexShrink: 0,
                                                borderColor: '#bdbdbd',
                                                color: '#666 !important',
                                                transition: 'all 0.2s ease',
                                                '&:hover': { borderColor: '#9e9e9e', bgcolor: '#f5f5f5' },
                                            }}
                                        >
                                            주소검색
                                        </Button>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <FormTextField
                            label={t('mall.customerCenterNumber')}
                            name={'customerCenterNumber'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={'02-0000-0000'}
                            fullWidth
                            required
                            rules={[
                                v => (!!v && !!v.trim()) || t('mall.customerCenterNumberRequired'),
                            ]}
                            onChange={(value: string) => {
                                form.setValue('customerCenterNumber', formatPhoneNumber(value || ''), {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                            }}
                        />
                        {businessType === 'SELLER' && (
                            <Link
                                typography={'caption'}
                                href={props?.businessRegistrationCertificateUrl || '/'}
                                target={'_blank'}
                            >
                                {t('mall.viewExistingBusinessRegistration')}
                            </Link>
                        )}
                        <SquareThumbInput
                            label={t('mall.businessRegistrationCertificate')}
                            description={t('mall.businessRegistrationCertificateDescription')}
                            required
                            onChanged={(file) => {
                                setBrnPaperFile(file);
                            }}
                        />
                        {businessType === 'SELLER' && (
                            <Link
                                typography={'caption'}
                                href={props?.mailOrderSalesRegistrationCertificateUrl || '/'}
                                target={'_blank'}
                            >
                                {t('mall.viewExistingMailOrderRegistration')}
                            </Link>
                        )}
                        <SquareThumbInput
                            label={t('mall.mailOrderSalesRegistrationCertificate')}
                            description={t('mall.mailOrderSalesRegistrationCertificateDescription')}
                            required
                            onChanged={(file) => {
                                setMailOrderRegistrationPaperFile(file);
                            }}
                        />
                        <FormTextField
                            label={t('mall.managerContact')}
                            name={'managerPhoneNumber'}
                            form={form}
                            formTrigger={'change'}
                            defaultValue={''}
                            placeholder={'010-0000-0000'}
                            fullWidth
                            required
                            rules={[
                                (v) => (!!v && !!v.trim()) || t('mall.requiredField'),
                                (v) =>
                                    /^01([0|1|6|7|8|9])-?([0-9]{3,4})-?([0-9]{4})$/.test(v) ||
                                    t('mall.invalidPhoneNumberFormat'),
                            ]}
                            // disabled={
                            //     !!getFieldState('managerPhoneNumber').error ||
                            //     !!getFieldState('managerPhoneNumber').invalid ||
                            //     !watch().managerPhoneNumber
                            // }
                            onChange={(value: string) => {
                                form.setValue('managerPhoneNumber', formatPhoneNumber(value || ''), {
                                    shouldValidate: true,
                                    shouldDirty: true,
                                });
                                setIsConfirmedPhone(false)
                                setIsConfirmedCode(false);
                                setCodeRequestCount(0);
                            }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <LoadingButton
                                            variant="contained"
                                            isLoading={isWaitingPhoneCode}
                                            onClick={async (ev) => {
                                                ev.stopPropagation();
                                                ev.preventDefault();
                                                setIsWaitingPhoneCode(true);
                                                try {
                                                    const phoneNumber = getValues('managerPhoneNumber');
                                                    if (!phoneNumber) return;
                                                    await requestVerifyPhoneCode(phoneNumber);
                                                    resetVerifyTimer();
                                                    setPhoneCode('');
                                                    setCodeRequestCount(codeRequestCount + 1);
                                                    dialogOpen({
                                                        title: t('mall.verificationCodeSent'),
                                                        message: t('mall.verificationCodeSentMessage'),
                                                        closeText: t('common.close'),
                                                    });
                                                } catch (err: any) {
                                                    if (err?.data?.placeHolder || err?.placeHolder) {
                                                        toast.error(err?.data?.placeHolder || err?.placeHolder);
                                                    } else {
                                                        console.error(err);
                                                        toast.error(t('mall.verificationCodeRequestFailed'));
                                                    }
                                                    toast.error(t('mall.verificationCodeRequestFailed'));
                                                } finally {
                                                    setIsWaitingPhoneCode(false);
                                                }
                                            }}
                                        >
                                            {t('mall.requestVerificationCode')}
                                        </LoadingButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Collapse in={codeRequestCount > 0}>
                            <TextField
                                label={t('mall.contactVerificationCode')}
                                value={phoneCode}
                                onChange={(e: any) => {
                                    setPhoneCode(e.target.value);
                                }}
                                error={verifyLimitSec <= 0 && !isConfirmedCode}
                                fullWidth
                                helperText={
                                    verifyLimitSec <= 0 && !isConfirmedCode ? t('mall.verificationTimeExpired') : ''
                                }
                                InputProps={{
                                    readOnly: isConfirmedCode,
                                    endAdornment: (
                                        <>
                                            <InputAdornment position="start">
                                                {isConfirmedCode ? '' : displayLimitTime}
                                            </InputAdornment>
                                            {!isConfirmedCode && (
                                                <InputAdornment position="end">
                                                    <LoadingButton
                                                        isLoading={isWaitConfirmCode}
                                                        variant="contained"
                                                        disabled={
                                                            !(!!phoneCode && !!phoneCode.trim()) || isConfirmedCode
                                                        }
                                                        onClick={async () => {
                                                            setIsWaitConfirmCode(true);
                                                            try {
                                                                const phoneNumber = getValues('managerPhoneNumber');
                                                                if (!phoneNumber) {
                                                                    toast.error(t('mall.phoneNumberInvalid'));
                                                                    return;
                                                                }
                                                                await confirmVerifyPhoneCode(phoneNumber, phoneCode);
                                                                toast.success(t('mall.contactVerificationCompleted'));
                                                                setIsConfirmedCode(true);
                                                                setIsConfirmedPhone(true);
                                                            } catch (err: any) {
                                                                if (err.data?.placeHolder || err.placeHolder) {
                                                                    toast.error(
                                                                        err.data?.placeHolder || err.placeHolder,
                                                                    );
                                                                } else {
                                                                    console.error(err);
                                                                    toast.error(
                                                                        t('mall.verificationCodeConfirmationFailed'),
                                                                    );
                                                                }
                                                            } finally {
                                                                setIsWaitConfirmCode(false);
                                                            }
                                                        }}
                                                    >
                                                        {t('mall.confirmVerificationCode')}
                                                    </LoadingButton>
                                                </InputAdornment>
                                            )}
                                            {isConfirmedCode && <ConfirmIcon color={'success'} />}
                                        </>
                                    ),
                                }}
                            />
                        </Collapse>
                    </Grid>
                </Grid>
                {businessType !== 'SELLER' && (
                    <Box sx={{ mt: 1, p: 1.5, borderRadius: 1, bgcolor: '#FFF4E5' }}>
                        <Typography variant='body2' sx={{ color: '#663C00 !important' }}>
                            {t('mall.membershipTypeChangeNotice')}
                        </Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    isLoading={isSubmitting}
                    onClick={async () => {
                        await handleSubmit(formSubmit)();
                    }}
                >
                    {t('common.edit')}
                </LoadingButton>
                <LoadingButton
                    color={'error'}
                    onClick={() => {
                        dialogClose();
                    }}
                >
                    {t('common.cancel')}
                </LoadingButton>
            </DialogActions>
            <AddressSearchModal
                open={addressModalOpen}
                onClose={() => setAddressModalOpen(false)}
                onSelect={({ address, detail }) => {
                    const full = [address, detail].filter(Boolean).join(' ').trim();
                    form.setValue('companyAddress', full, { shouldDirty: true, shouldValidate: true });
                }}
            />
        </>
    );
};
